import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Upload, Loader2, Image, Check, AlertCircle, FileText, FileVideo, Trash2 } from 'lucide-react';
import { kreatorApi, type WpMediaItem } from '../lib/api';
import { useBackdropClickClose } from '../hooks/useBackdropClickClose';
import { ConfirmDialog } from './ConfirmDialog';
import { useAuthStore } from '../store/auth';

interface Props {
  integrationId: string;
  onSelect: (url: string, item?: WpMediaItem) => void;
  onSelectMultiple?: (items: WpMediaItem[]) => void;
  multiSelect?: boolean;
  mediaType?: 'image' | 'video' | 'pdf' | 'all';
  accept?: string;
  title?: string;
  uploadLabel?: string;
  emptyLabel?: string;
  emptyHint?: string;
  onClose: () => void;
}

const PER_PAGE = 30;

function VideoThumbnail({ item }: { item: WpMediaItem }) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: '160px' },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <span ref={containerRef} className="relative flex h-full w-full items-center justify-center bg-surface-raised">
      {shouldLoad ? (
        <video
          ref={videoRef}
          src={item.src}
          muted
          playsInline
          preload="metadata"
          onLoadedMetadata={() => {
            const video = videoRef.current;
            if (video && video.currentTime === 0) {
              try {
                video.currentTime = 0.001;
              } catch {
                // Some browsers expose metadata before the first seekable range.
              }
            }
          }}
          className="h-full w-full object-cover"
        />
      ) : (
        <FileVideo className="h-7 w-7 text-accent-primary" />
      )}
      <span className="pointer-events-none absolute bottom-1 left-1 rounded bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white">
        MP4
      </span>
    </span>
  );
}

export function WpMediaPickerModal({
  integrationId,
  onSelect,
  onSelectMultiple,
  multiSelect = false,
  mediaType = 'image',
  accept,
  title,
  uploadLabel,
  emptyLabel,
  emptyHint,
  onClose,
}: Props) {
  const [items, setItems] = useState<WpMediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<WpMediaItem | null>(null);
  const [selectedMultiple, setSelectedMultiple] = useState<WpMediaItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WpMediaItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const role = useAuthStore(state => state.user?.role);
  const canDelete = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const requestIdRef = useRef(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Refs for scroll handler to avoid stale closures
  const stateRef = useRef({ page, search, hasMore, loadingMore });
  stateRef.current = { page, search, hasMore, loadingMore };
  const fetchMediaRef = useRef<(sq: string, pg: number, append: boolean) => void>();

  const mimeType = mediaType === 'pdf'
    ? 'application/pdf'
    : mediaType === 'image'
      ? 'image'
      : mediaType === 'video'
        ? 'video'
        : 'all';
  const acceptedMimeTypes = mediaType === 'pdf'
    ? ['application/pdf']
    : mediaType === 'image'
      ? ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']
      : mediaType === 'video'
        ? ['video/mp4']
        : ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'video/mp4', 'application/pdf'];
  const acceptValue = accept ?? (
    mediaType === 'pdf'
      ? 'application/pdf,.pdf'
      : mediaType === 'video'
        ? 'video/mp4,.mp4'
        : mediaType === 'all'
          ? 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml,video/mp4,application/pdf,.mp4,.pdf'
          : 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml'
  );
  const modalTitle = title ?? (
    mediaType === 'pdf'
      ? 'Biblioteka PDF WordPress'
      : mediaType === 'video'
        ? 'Biblioteka wideo WordPress'
        : 'Biblioteka mediów WordPress'
  );
  const uploadButtonLabel = uploadLabel ?? 'Wgraj';
  const emptyText = emptyLabel ?? (
    mediaType === 'pdf'
      ? 'Brak plików PDF w bibliotece mediów'
      : mediaType === 'video'
        ? 'Brak filmów MP4 w bibliotece mediów'
        : mediaType === 'all'
          ? 'Brak plików w bibliotece mediów'
          : 'Brak obrazów w bibliotece mediów'
  );
  const emptySubtext = emptyHint ?? (
    mediaType === 'pdf'
      ? 'Przeciągnij PDF tutaj albo użyj przycisku Wgraj'
      : mediaType === 'video'
        ? 'Przeciągnij MP4 tutaj albo użyj przycisku Wgraj'
        : mediaType === 'all'
          ? 'Przeciągnij pliki tutaj albo użyj przycisku Wgraj'
          : 'Przeciągnij obrazy tutaj albo użyj przycisku Wgraj'
  );

  const fetchMedia = useCallback(async (searchQuery = '', pageNum = 1, append = false) => {
    const requestId = ++requestIdRef.current;
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const res = await kreatorApi.getWpMedia(integrationId, {
        per_page: PER_PAGE,
        page: pageNum,
        search: searchQuery || undefined,
        mime_type: mimeType,
      });
      const newItems = res.data;
      if (requestId !== requestIdRef.current) return;
      if (append) {
        setItems((prev) => [...prev, ...newItems]);
      } else {
        setItems(newItems);
      }
      setHasMore(newItems.length >= PER_PAGE);
      setPage(pageNum);
    } catch {
      if (requestId !== requestIdRef.current) return;
      if (!append) setItems([]);
      setHasMore(false);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [integrationId, mimeType]);

  fetchMediaRef.current = fetchMedia;

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  // Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleteTarget) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, deleteTarget]);

  useEffect(() => () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); }, []);

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await kreatorApi.deleteWpMedia(integrationId, deleteTarget.id);
      setSelected(current => current?.id === deleteTarget.id ? null : current);
      setSelectedMultiple(current => current.filter(item => item.id !== deleteTarget.id));
      setItems(current => current.filter(item => item.id !== deleteTarget.id));
      setDeleteTarget(null);
      // Restart offset pagination after removal so the next page cannot skip a file.
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      await fetchMedia(search, 1, false);
    } catch (error) {
      setDeleteError((error as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się usunąć pliku.');
    } finally { setDeleting(false); }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchMedia(value, 1, false);
    }, 400);
  };

  // Infinite scroll via scroll event on the container
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const { hasMore: hm, loadingMore: lm, page: pg, search: sq } = stateRef.current;
      if (!hm || lm) return;
      if (scrollHeight - scrollTop - clientHeight < 300) {
        fetchMediaRef.current?.(sq, pg + 1, true);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-load more if content doesn't fill the container (no scrollbar = scroll never fires)
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    // Wait for images to render and potentially affect scrollHeight
    const timer = setTimeout(() => {
      if (container.scrollHeight <= container.clientHeight) {
        fetchMedia(search, page + 1, true);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [loading, loadingMore, hasMore, items.length, fetchMedia, search, page]);

  const handleUpload = async (file: File): Promise<string | null> => {
    setUploadCount((c) => c + 1);
    setUploadError(null);
    try {
      const res = await kreatorApi.uploadMedia(integrationId, file);
      setItems((prev) => [res.data, ...prev]);
      if (multiSelect) {
        setSelectedMultiple((prev) => [...prev, res.data]);
      } else {
        setSelected(res.data);
      }
      return null;
    } catch (err) {
      console.error('Upload error:', err);
      const message = (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się wgrać pliku';
      setUploadError(message);
      return message;
    } finally {
      setUploadCount((c) => c - 1);
    }
  };

  const handleUploadFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;

    const selectedFiles = Array.from(files).filter((file) => {
      if (acceptedMimeTypes.includes(file.type)) return true;
      if ((mediaType === 'video' || mediaType === 'all') && /\.mp4$/i.test(file.name)) return true;
      return false;
    });
    if (selectedFiles.length === 0) {
      setUploadError(
        mediaType === 'pdf'
          ? 'Możesz wgrać tylko pliki PDF.'
          : mediaType === 'video'
            ? 'Możesz wgrać tylko pliki MP4.'
            : mediaType === 'all'
              ? 'Możesz wgrać tylko obsługiwane pliki graficzne, MP4 lub PDF.'
              : 'Możesz wgrać tylko pliki graficzne.',
      );
      return;
    }
    setUploadError(null);
    setUploadProgress({ done: 0, total: selectedFiles.length });

    let failed = 0;
    let lastError: string | null = null;
    for (const file of selectedFiles) {
      try {
        const error = await handleUpload(file);
        if (error) {
          failed += 1;
          lastError = error;
        }
      } catch {
        failed += 1;
      } finally {
        setUploadProgress((prev) => prev ? { ...prev, done: prev.done + 1 } : null);
      }
    }

    setUploadProgress(null);

    if (failed > 0) {
      const summary = failed === 1
        ? 'Nie udało się wgrać 1 pliku.'
        : `Nie udało się wgrać ${failed} plików.`;
      setUploadError(lastError ? `${summary} ${lastError}` : summary);
      await fetchMedia(search, 1, false);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragActive) setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const nextTarget = e.relatedTarget as Node | null;
    if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    void handleUploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleItemClick = (item: WpMediaItem, e: React.MouseEvent) => {
    if (multiSelect) {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;
      if (isCtrlOrCmd) {
        setSelectedMultiple((prev) => {
          const exists = prev.some((i) => i.id === item.id);
          return exists ? prev.filter((i) => i.id !== item.id) : [...prev, item];
        });
      } else {
        setSelectedMultiple((prev) => {
          const exists = prev.some((i) => i.id === item.id);
          return exists ? prev.filter((i) => i.id !== item.id) : [...prev, item];
        });
      }
    } else {
      setSelected(item);
    }
  };

  const isItemSelected = (item: WpMediaItem) => {
    if (multiSelect) {
      return selectedMultiple.some((i) => i.id === item.id);
    }
    return selected?.id === item.id;
  };

  const handleConfirm = () => {
    if (multiSelect && onSelectMultiple) {
      onSelectMultiple(selectedMultiple);
    } else if (selected) {
      onSelect(selected.src, selected);
    }
  };

  const selectionCount = multiSelect ? selectedMultiple.length : (selected ? 1 : 0);
  const uploading = uploadCount > 0;
  const isImageItem = (item: WpMediaItem) => (item.mimeType || '').startsWith('image/');
  const isVideoItem = (item: WpMediaItem) => (item.mimeType || '').startsWith('video/');
  const backdropProps = useBackdropClickClose(onClose, !deleteTarget);
  const selectedFallback = mediaType === 'video'
    ? 'Wybrane wideo'
    : mediaType === 'pdf'
      ? 'Wybrany plik PDF'
      : mediaType === 'all'
        ? 'Wybrany plik'
        : 'Wybrany obraz';
  const selectionPrompt = mediaType === 'video'
    ? 'Wybierz wideo z biblioteki'
    : mediaType === 'pdf'
      ? 'Wybierz plik PDF z biblioteki'
      : mediaType === 'all'
        ? 'Wybierz plik z biblioteki'
        : 'Wybierz obraz z biblioteki';

  // Render as portal to document.body so z-index works above editor toolbar portals (z-index 9999)
  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" {...backdropProps} />
      <div className="relative bg-surface-base border border-border rounded-2xl w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-text-primary">
            {modalTitle}
            {items.length > 0 && <span className="ml-2 text-sm font-normal text-text-secondary">({items.length}{hasMore ? '+' : ''})</span>}
          </h2>
          <button type="button" onClick={onClose} aria-label="Zamknij bibliotekę mediów" className="text-text-secondary hover:text-text-primary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-border-subtle">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Szukaj w mediach..."
              className="input w-full pl-9 text-sm"
            />
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploadProgress ? `Wgrywam ${uploadProgress.done}/${uploadProgress.total}` : uploadButtonLabel}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={acceptValue}
            multiple
            className="hidden"
            onChange={(e) => {
              void handleUploadFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {/* Upload error */}
        {uploadError && (
          <div className="flex items-center gap-2 px-6 py-2 bg-danger/10 border-b border-danger/20 text-danger text-xs">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {uploadError}
            <button type="button" onClick={() => setUploadError(null)} aria-label="Zamknij komunikat" className="ml-auto text-danger hover:text-danger">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Grid */}
        <div
          ref={scrollContainerRef}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative flex-1 min-h-0 overflow-y-auto p-6 border-2 border-dashed transition-colors ${
            dragActive
              ? 'border-accent-primary bg-accent-primary/10'
              : 'border-transparent'
          }`}
        >
          {dragActive && (
            <div className="pointer-events-none absolute inset-4 z-10 rounded-xl border border-accent-primary/40 bg-surface-base/80 backdrop-blur-sm flex flex-col items-center justify-center text-center">
              <Upload className="w-8 h-8 text-accent-primary mb-2" />
              <p className="text-sm font-semibold text-text-primary">
                {mediaType === 'pdf'
                  ? 'Upuść PDF, aby wgrać go do WordPress'
                  : mediaType === 'video'
                    ? 'Upuść MP4, aby wgrać go do WordPress'
                    : mediaType === 'all'
                      ? 'Upuść pliki, aby wgrać je do WordPress'
                      : 'Upuść obrazy, aby wgrać je do WordPress'}
              </p>
              <p className="text-xs text-text-secondary mt-1">Pliki pojawią się na początku biblioteki mediów</p>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-text-muted">
              {mediaType === 'pdf'
                ? <FileText className="w-12 h-12 mb-3" />
                : mediaType === 'video'
                  ? <FileVideo className="w-12 h-12 mb-3" />
                  : <Image className="w-12 h-12 mb-3" />}
              <p className="text-sm">{emptyText}</p>
              <p className="text-xs mt-1">{emptySubtext}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-3">
                {items.map((item) => (
                  <div key={item.id} className="relative aspect-square">
                  <button
                    type="button"
                    aria-label={`Wybierz: ${item.name || item.alt || 'plik'}`}
                    onClick={(e) => handleItemClick(item, e)}
                    className={`relative block w-full h-full rounded-lg overflow-hidden border-2 transition-all ${
                      isItemSelected(item)
                        ? 'border-accent-primary ring-2 ring-accent-primary/30'
                        : 'border-border hover:border-border-strong'
                    }`}
                  >
                    {isImageItem(item) ? (
                      <img
                        src={item.src}
                        alt={item.alt || item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : isVideoItem(item) ? (
                      <VideoThumbnail item={item} />
                    ) : (
                      <span className="flex h-full w-full flex-col items-center justify-center gap-2 bg-surface-raised p-2 text-center text-[10px] text-text-secondary">
                        <FileText className="h-7 w-7 text-accent-primary" />
                        <span className="line-clamp-2 break-all">{item.name || 'PDF'}</span>
                      </span>
                    )}
                    {multiSelect && isItemSelected(item) && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-accent-primary flex items-center justify-center">
                        <Check className="w-3 h-3 text-dark-900" />
                      </div>
                    )}
                  </button>
                  {canDelete && <button type="button" className="absolute bottom-1 right-1 p-1.5 rounded-lg bg-surface-base text-danger hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-accent-primary" aria-label={`Usuń z biblioteki: ${item.name || item.alt || 'plik'}`} title="Usuń z biblioteki" onClick={() => { setDeleteError(null); setDeleteTarget(item); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>}
                  </div>
                ))}
              </div>
              {/* Loading indicator for next page */}
              {loadingMore && (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
                </div>
              )}
              {!hasMore && items.length > PER_PAGE && (
                <div className="text-center text-xs text-text-muted py-4">
                  Wszystkie pliki załadowane ({items.length})
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center px-6 py-4 border-t border-border">
          <button
            onClick={handleConfirm}
            disabled={selectionCount === 0}
            className="btn-primary text-sm shrink-0 disabled:opacity-50"
          >
            Wybierz
          </button>
          <div className="flex-1 text-center text-sm text-text-secondary truncate px-4">
            {multiSelect
              ? (selectionCount > 0 ? `Wybrano: ${selectionCount}` : 'Kliknij, aby wybrać pliki')
              : (selected ? selected.name || selected.alt || selectedFallback : selectionPrompt)}
          </div>
          <button
            onClick={onClose}
            className="btn-secondary text-sm shrink-0"
          >
            Anuluj
          </button>
        </div>
      </div>
      <ConfirmDialog isOpen={deleteTarget !== null} title="Usunąć plik z biblioteki?" message={<>Plik <strong>{deleteTarget?.name || deleteTarget?.alt || 'bez nazwy'}</strong> zostanie trwale usunięty z WordPress. Przestanie być dostępny także na stronach i produktach, które go używają.{deleteError && <p role="alert" className="text-danger mt-3">{deleteError}</p>}</>} confirmLabel="Usuń z biblioteki" onConfirm={() => void handleDelete()} onClose={() => setDeleteTarget(null)} isPending={deleting} />
    </div>,
    document.body,
  );
}
